/**
 * Review one captured trail.
 *
 * Rewritten to lead with human-readable step descriptions instead of raw
 * graph IDs, editable symptom + summary, role-aware footer actions, step
 * reorder/delete, and compact mark controls.
 *
 * The step list and outcome selector are shared with LearningMode via
 * TrailSteps.jsx — extracted so both layouts use identical rendering.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Loader2 } from "lucide-react";
import { getTrail, markSteps, discardTrail } from "./useLearning";
import { authFetch } from "../../utils/auth";
import { getStoredRole } from "../../utils/auth";
import {
  StepList,
  OutcomeSelector,
  extractorSuggestedOutcome,
  stepResultNote,
} from "./TrailSteps";
import CaseRecord from "./CaseRecord";
import TrailGraph, { TrailViewToggle, TRAIL_VIEWS, useTrailView } from "./TrailGraph";
import TurnBreakdown from "./TurnBreakdown";
import { modesLabel } from "./modeLabels";
import "./styles/scrollbars.css";
import LearningTranscript from "./LearningTranscript";
import { turnsToTranscript } from "./transcriptAdapter";

/* ── local API helpers (new endpoints, can't edit useLearning.js) ───────── */
const BASE = "/api/learning";
async function api(path, options) {
  const res = await authFetch(`${BASE}${path}`, options);
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail || body.message;
    const msg = typeof detail === "string" ? detail : `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return res.json();
}
const patchTrail = (id, body) =>
  api(`/trails/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
const deleteStep = (id, stepNo) =>
  api(`/trails/${id}/steps/${stepNo}`, { method: "DELETE" });
const reorderSteps = (id, order) =>
  api(`/trails/${id}/steps/reorder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ order }),
  });
const submitTrail = (id, outcome) =>
  api(`/trails/${id}/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ outcome }),
  });
const approveTrail = (id) =>
  api(`/trails/${id}/approve`, { method: "POST" });
const rejectTrail = (id, note) =>
  api(`/trails/${id}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note }),
  });

/* ── constants ──────────────────────────────────────────────────────────── */
const ADMIN_ROLES = new Set(["admin", "super_admin"]);

/* ── component ──────────────────────────────────────────────────────────── */
const TrailReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [results, setResults] = useState({});
  /* The optional one-line "why" behind a Ruled out mark, { [step_no]: text }.
     Seeded from the stored notes and persisted by the SAME debounced PATCH as
     the marks — a note is never a request of its own. */
  const [notes, setNotes] = useState({});
  const [outcome, setOutcome] = useState("");
  const [activeTurn, setActiveTurn] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // List vs trace-flow Graph — shared preference with the in-session overlay.
  const [trailView, setTrailView] = useTrailView();

  /* The case record owns its own state and its own saving (CaseRecord.jsx) —
     the page only holds the ref it flushes before submitting, and the problem
     line, which the header echoes so the two can never disagree. */
  const caseRef = useRef(null);
  const [problemLine, setProblemLine] = useState("");

  // Explicit outcome confirmation — capture pre-fills an inferred outcome
  // with the radio already checked, so the old "choose an outcome" gate was
  // unreachable dead code. This checkbox forces the engineer to say "yes,
  // this is right" even when it was pre-filled.
  const [outcomeConfirmed, setOutcomeConfirmed] = useState(false);

  // Reject modal
  const [showReject, setShowReject] = useState(false);
  const [rejectNote, setRejectNote] = useState("");

  // Steps as local mutable array (for reorder/delete)
  const [localSteps, setLocalSteps] = useState([]);

  const role = getStoredRole();
  const isAdmin = ADMIN_ROLES.has(role);
  const isOwner = useMemo(() => {
    if (!data) return false;
    const owner = data.trail?.owner;
    const me = localStorage.getItem("email") || localStorage.getItem("username") || "";
    return !owner || owner === me;
  }, [data]);

  const status = data?.trail?.status || "draft";
  const readOnly = status === "published" || (status === "submitted" && !isAdmin);
  const canEdit = status === "draft" && isOwner;

  useEffect(() => {
    (async () => {
      try {
        const found = await getTrail(id);
        if (!found) {
          setError("Learning module is disabled.");
          return;
        }
        setData(found);
        setLocalSteps(found.steps || []);
        // The extractor's suggestion (e.g. "cleared with no change made")
        // outranks capture's inferred outcome but never a human's choice.
        const prefilled =
          extractorSuggestedOutcome(found.trail) ||
          (found.trail.outcome && found.trail.outcome !== "unknown"
            ? found.trail.outcome
            : "");
        setOutcome(prefilled);
        // If the outcome was NOT pre-filled, auto-confirm is moot; if it
        // was, the engineer must tick the checkbox.
        setOutcomeConfirmed(!prefilled);
        setResults(
          Object.fromEntries(
            (found.steps || [])
              .filter((s) => s.result && s.result !== "unknown")
              .map((s) => [s.step_no, s.result])
          )
        );
        // Same shape, same rule: only the steps that actually carry one, so an
        // untouched trail sends no notes at all.
        setNotes(
          Object.fromEntries(
            (found.steps || [])
              .map((s) => [s.step_no, stepResultNote(s)])
              .filter(([, note]) => !!note)
          )
        );
        setProblemLine(
          found.trail.problem_definition || found.trail.symptom_text || ""
        );
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [id]);

  // Only steps that actually ask a question count. The server classifies each
  // step (`markable`) and its submit gate counts the same way — computing this
  // differently here would disable the button on a trail the API would accept.
  // `markable !== false` so a response from an older backend, which has no such
  // field, still behaves as it did before.
  const unmarked = useMemo(
    () =>
      localSteps.filter((s) => s.markable !== false && !results[s.step_no]).length,
    [localSteps, results]
  );

  /* ── the real conversation ──
     `data.turns` is the session's stored turn envelopes (server-side
     learning/transcript.py). `data.turns_unavailable` is true when the read
     FAILED (timeout, pool error) — distinct from a session that genuinely has
     no stored turns (turns is [] and turns_unavailable is absent/false). */
  /* The extractor's per-turn output. Read the same way the in-session overlay
     reads it — nested under the trail on newer responses, top-level on older
     ones — and rendered read-only: the approver has to be able to see what the
     extractor concluded at each turn before approving it. */
  const turnState = useMemo(() => {
    const ts = data?.trail?.turn_state ?? data?.turn_state;
    return ts !== undefined ? ts : undefined;
  }, [data]);

  const groupedTurns = useMemo(() => turnsToTranscript(data?.turns), [data]);
  const hasTranscript = groupedTurns.length > 0;
  const turnsUnavailable = !!data?.turns_unavailable;
  const [retryingTurns, setRetryingTurns] = useState(false);

  const retryTranscript = useCallback(async () => {
    setRetryingTurns(true);
    try {
      const fresh = await getTrail(id);
      if (fresh) {
        setData((prev) => ({
          ...prev,
          turns: fresh.turns,
          turns_unavailable: fresh.turns_unavailable,
        }));
      }
    } catch {
      /* leave as-is; button stays visible */
    } finally {
      setRetryingTurns(false);
    }
  }, [id]);

  /* Right pane reports the turn it scrolled to, so the step list follows. */
  const onTurnInView = useCallback((turnNo) => {
    setActiveTurn((prev) => (prev === turnNo ? prev : turnNo));
  }, []);

  /* ── why can't we submit? ── */
  const submitBlockReasons = useMemo(() => {
    const reasons = [];
    if (unmarked > 0) {
      reasons.push({
        key: "unmarked",
        text: `${unmarked} step${unmarked > 1 ? "s" : ""} still need${unmarked > 1 ? "" : "s"} a mark`,
        target: "[data-unmarked='true']",
      });
    }
    if (!outcome) reasons.push({ key: "outcome", text: "Choose an outcome before submitting", target: "#tr-outcome" });
    if (outcome && !outcomeConfirmed) {
      reasons.push({ key: "confirm", text: "Confirm the outcome is correct", target: "#tr-outcome" });
    }
    return reasons;
  }, [unmarked, outcome, outcomeConfirmed]);

  const canSubmit = data && submitBlockReasons.length === 0 && !busy;

  /* Scroll the first offender into view and flash it — ported from
     LearningMode so the block reasons are clickable here too. */
  const scrollRef = useRef(null);
  const flashTimer = useRef(null);
  useEffect(() => () => clearTimeout(flashTimer.current), []);
  const jumpTo = useCallback((selector) => {
    const root = scrollRef.current;
    const el = root?.querySelector(selector);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove("tr-flash");
    void el.offsetWidth;
    el.classList.add("tr-flash");
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => el.classList.remove("tr-flash"), 1500);
  }, []);

  /* ── save marks (existing endpoint) ── */
  const saveMarks = useCallback(async () => {
    /* Bail only when there is NOTHING to say. A note changing on its own is a
       real change — the endpoint still requires `results`, so an empty results
       map is the only thing that cannot be sent. */
    if (!Object.keys(results).length && !Object.keys(notes).length) return;
    await markSteps(id, results, notes);
  }, [id, results, notes]);

  /* ── save the case record ──
     CaseRecord keeps the text; this only hands it the PATCH. It never throws:
     a rejected save is reported inside the block and the text stays put. */
  const saveCase = useCallback(
    async () => (caseRef.current ? caseRef.current.flush() : { status: "noop" }),
    []
  );

  // Track whether marks have been changed by the user (not just loaded).
  const marksDirtyRef = useRef(false);

  /* ── marking ──
     One handler for BOTH views: the list and the trace-flow graph write the
     same `results` map, which the debounced effect below persists through the
     single markSteps() PATCH. */
  const markStep = useCallback((stepNo, value) => {
    marksDirtyRef.current = true;
    setResults((p) => ({ ...p, [stepNo]: value }));
  }, []);

  /* The note is stored beside the mark and saved with it. An empty string is
     kept in the map on purpose: it is how a cleared note reaches the backend,
     which deletes the key rather than storing "". */
  const noteStep = useCallback((stepNo, value) => {
    marksDirtyRef.current = true;
    setNotes((p) => ({ ...p, [stepNo]: value }));
  }, []);

  /* ── step reorder ── */
  const moveStep = useCallback(
    async (idx, dir) => {
      const target = idx + dir;
      if (target < 0 || target >= localSteps.length) return;
      const next = [...localSteps];
      [next[idx], next[target]] = [next[target], next[idx]];
      setLocalSteps(next);
      try {
        await reorderSteps(id, next.map((s) => s.step_no));
      } catch {
        // revert on failure
        setLocalSteps(localSteps);
      }
    },
    [id, localSteps]
  );

  /* ── step delete ── */
  const removeStep = useCallback(
    async (stepNo) => {
      const prev = localSteps;
      setLocalSteps((s) => s.filter((x) => x.step_no !== stepNo));
      try {
        await deleteStep(id, stepNo);
      } catch {
        setLocalSteps(prev);
      }
    },
    [id, localSteps]
  );

  /* ── footer actions ── */
  const onSubmit = async () => {
    setBusy(true);
    setError("");
    try {
      await saveMarks();
      // Do not submit on top of edits that could not be written.
      const saved = await saveCase();
      if (saved.status === "failed") {
        throw new Error("Your edits could not be saved — nothing was submitted. Try again.");
      }
      await submitTrail(id, outcome);
      navigate("/learning");
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const onDiscard = async () => {
    setBusy(true);
    try {
      await discardTrail(id);
      navigate("/learning");
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const onApprove = async () => {
    setBusy(true);
    setError("");
    try {
      await approveTrail(id);
      navigate("/learning");
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const onReject = async () => {
    if (!rejectNote.trim()) return;
    setBusy(true);
    setError("");
    try {
      await rejectTrail(id, rejectNote.trim());
      navigate("/learning");
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  /* ── auto-save marks on change (debounced, only when user has changed something) ── */
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!data || readOnly || !marksDirtyRef.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveMarks().catch(() => {});
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [results, notes, data, readOnly, saveMarks]);

  /* ── loading / error state ── */
  if (error && !data) {
    return (
      <div className="tr-center-wrap">
        <div className="tr-center-card">
          <p className="tr-error-text">{error}</p>
          <button type="button" onClick={() => navigate("/learning")} className="tr-link">
            Back to trails
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="tr-center-wrap">
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      </div>
    );
  }

  const { trail, corrections } = data;

  return (
    <div className="tr-root">
      {/* ── header ── */}
      <header className="tr-header">
        <button
          type="button"
          onClick={() => navigate("/learning")}
          className="tr-back-btn"
          aria-label="Back to trails"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="tr-header-body">
          {/* The symptom is edited in the case block below — the header just
              reflects it so the two can never disagree. */}
          <h1 className={`tr-symptom-ro ${problemLine ? "" : "tr-muted"}`}>
            {problemLine || "Untitled trail"}
          </h1>
          <p className="tr-meta">
            {trail.session_id} · {modesLabel(trail.modes)} · {localSteps.length} steps
            {status !== "draft" && (
              <span className={`tr-status-badge tr-status-badge--${status}`}>{status}</span>
            )}
          </p>
        </div>
        {!readOnly && (
          <div className="tr-header-right">
            {unmarked > 0
              ? `${unmarked} step${unmarked > 1 ? "s" : ""} unmarked`
              : "All steps marked"}
          </div>
        )}
      </header>

      {/* ── review note banner (rejected trail returned to draft) ── */}
      {trail.review_note && status === "draft" && (
        <div className="tr-review-note">
          <AlertTriangle size={15} />
          <div>
            <strong>Returned by reviewer:</strong> {trail.review_note}
          </div>
        </div>
      )}

      {/* ── submitted, owner view ── */}
      {status === "submitted" && !isAdmin && (
        <div className="tr-info-banner">
          Waiting for admin approval. You cannot edit this trail until it is returned.
        </div>
      )}

      {/* ── published badge ── */}
      {status === "published" && (
        <div className="tr-info-banner tr-info-banner--published">
          This trail has been published and is now part of the learning corpus.
        </div>
      )}

      <main className="tr-main" ref={scrollRef}>
        {/* ── left pane: the trail ── */}
        <section className="tr-trail-pane">
          {/* ── the case record (problem / symptoms / facts / causes / resolution) ── */}
          <CaseRecord
            ref={caseRef}
            trail={trail?.intent === "informational"
              ? { ...trail, entries: trail.entries ?? data?.entries ?? [] }
              : trail}
            steps={localSteps}
            readOnly={!canEdit}
            idPrefix="tr"
            onSave={(patch) => patchTrail(id, patch)}
            onProblemChange={setProblemLine}
          />
          {/* TODO: informational trails render the synthesis/entries view
              and replace the diagnostic case record entirely (CaseRecord
              branches on intent). The admin screen has no tabs, so there is
              no way to get back to the diagnostic layout. A lightweight
              solution: add a view toggle (not a full tab system) similar to
              the Preview/Edit toggle, or render both sections stacked with
              the informational one first. Do not add tabs without asking. */}

          {/* ── turn-by-turn breakdown of the case record (read-only) ── */}
          {turnState !== undefined && (
            <div className="tr-section">
              <TurnBreakdown turnState={turnState} turns={data?.turns} />
            </div>
          )}

          {/* ── steps (shared component) ── */}
          <div className="tr-section">
            <div className="tr-section-head">
              <h2 className="tr-heading">
                What happened, in order
                {localSteps.length > 0 && (
                  <span className="tr-heading-count">
                    {localSteps.length} step{localSteps.length > 1 ? "s" : ""}
                  </span>
                )}
              </h2>
              <TrailViewToggle view={trailView} onChange={setTrailView} idPrefix="tr-view" />
            </div>
            {trailView === TRAIL_VIEWS.GRAPH ? (
              /* Same steps, same `results`, same mark handler as the list —
                 only the rendering swaps. `hybrid` is absent on this screen
                 (no fast Q&A on the standalone review), so it defaults to the
                 empty sentinel inside TrailGraph. */
              <TrailGraph
                steps={localSteps}
                results={results}
                notes={notes}
                readOnly={readOnly}
                activeTurn={activeTurn}
                onMark={markStep}
                onNote={noteStep}
                onStepClick={setActiveTurn}
              />
            ) : (
              <StepList
                steps={localSteps}
                results={results}
                notes={notes}
                readOnly={readOnly}
                canEdit={canEdit}
                activeTurn={activeTurn}
                onMark={markStep}
                onNote={noteStep}
                onStepClick={setActiveTurn}
                onMoveStep={moveStep}
                onRemoveStep={removeStep}
              />
            )}
          </div>

          {/* ── outcome selector (shared component) ── */}
          <div className="tr-section tr-outcome-section" id="tr-outcome">
            <p className="tr-section-label">How did it end?</p>
            <OutcomeSelector
              outcome={outcome}
              readOnly={readOnly}
              suggested={
                extractorSuggestedOutcome(data?.trail) ||
                (data?.trail?.outcome_source === "inferred" ? data.trail.outcome : null)
              }
              onChange={(val) => {
                setOutcome(val);
                // A deliberate pick counts as confirmed.
                setOutcomeConfirmed(true);
              }}
            />
            {/* Explicit confirmation when capture pre-filled the outcome */}
            {!readOnly && outcome && !outcomeConfirmed && (
              <label className="tr-outcome-confirm">
                <input
                  type="checkbox"
                  checked={outcomeConfirmed}
                  onChange={(e) => setOutcomeConfirmed(e.target.checked)}
                />
                <span>I confirm this outcome is correct</span>
              </label>
            )}
          </div>
        </section>

        {/* ── right pane: transcript + corrections ── */}
        <section
          className={`tr-evidence-pane${hasTranscript ? " tr-evidence-pane--live" : ""}`}
        >
          <p className="tr-section-label">Transcript</p>
          <p className="tr-callout">
            Read this before marking. Clicking a step highlights its turn.
            {!hasTranscript && !turnsUnavailable &&
              " This session's conversation is no longer stored, so only the trail's own step text is shown."}
          </p>

          {turnsUnavailable && !hasTranscript && (
            <div className="tr-callout tr-callout--warn" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <AlertTriangle size={16} />
              <span>
                Could not load the conversation for this trail.
              </span>
              <button
                className="tr-retry-btn"
                onClick={retryTranscript}
                disabled={retryingTurns}
                style={{
                  marginLeft: 8, padding: "2px 10px", borderRadius: 4,
                  border: "1px solid var(--border, #ccc)", background: "var(--bg-surface, #f5f5f5)",
                  cursor: retryingTurns ? "wait" : "pointer", fontSize: "0.85em",
                }}
              >
                {retryingTurns ? "Retrying…" : "Retry"}
              </button>
            </div>
          )}

          {hasTranscript ? (
            /* The conversation as it was on screen — same component, same
               dashboard5 message classes as the in-session overlay.
               Corrections ride along attached to their turn (see below). */
            <div className="tr-transcript-live">
              <LearningTranscript
                groupedTurns={groupedTurns}
                sessionId={trail.session_id}
                corrections={corrections}
                activeTurn={activeTurn}
                onTurnClick={setActiveTurn}
                onTurnInView={onTurnInView}
              />
            </div>
          ) : (
            <div className="tr-transcript">
              {localSteps.map((s) => (
                <div
                  key={s.step_no}
                  className={`tr-turn ${
                    activeTurn != null && s.turn_no === activeTurn ? "tr-turn--active" : ""
                  }`}
                >
                  <span className="tr-turn-label">turn {s.turn_no ?? "—"}</span>
                  <p className="tr-turn-text">{s.proposed_text || s.query_text || "—"}</p>
                  {(s.sources_shown || []).length > 0 && (
                    <p className="tr-turn-sources">{s.sources_shown.join(" · ")}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── corrections ──
              Only when there is NO transcript. With one, LearningTranscript
              already renders every correction against the turn it was reported
              on (and the unmatched ones in its own trailing section), which is
              where a reviewer needs them — next to the answer that was wrong.
              Keeping this block too would print all of them twice. */}
          {!hasTranscript && (corrections || []).length > 0 && (
            <div className="tr-corrections">
              <p className="tr-section-label tr-section-label--rose">
                Reported wrong ({corrections.length})
              </p>
              {corrections.map((c) => (
                <div key={c.id} className="tr-correction">
                  <p className="tr-correction-text">{c.wrong_text}</p>
                  <p className="tr-correction-reason">{c.why_wrong}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* ── footer ── */}
      <footer className="tr-footer">
        {error && <span className="tr-error-text">{error}</span>}

        {/* draft + owner */}
        {status === "draft" && isOwner && (
          <>
            {submitBlockReasons.length > 0 && (
              <span className="tr-block-hint">
                {submitBlockReasons.map((r, i) => (
                  <React.Fragment key={r.key}>
                    {i > 0 && <span className="tr-block-sep">·</span>}
                    <button
                      type="button"
                      className="tr-block-hint-btn"
                      onClick={() => jumpTo(r.target)}
                      title={`Go to ${r.text.toLowerCase()}`}
                    >
                      {r.text}
                    </button>
                  </React.Fragment>
                ))}
              </span>
            )}
            {submitBlockReasons.length === 0 && (
              <span className="tr-ready-hint">Ready to submit</span>
            )}
            <div className="tr-footer-actions">
              <button type="button" onClick={onDiscard} disabled={busy} className="tr-btn tr-btn--secondary">
                Discard
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit}
                className="tr-btn tr-btn--primary"
                title={submitBlockReasons.length > 0 ? submitBlockReasons.map((r) => r.text).join(". ") : undefined}
              >
                {busy ? "Saving..." : "Submit for approval"}
              </button>
            </div>
          </>
        )}

        {/* submitted + admin */}
        {status === "submitted" && isAdmin && (
          <>
            <span className="tr-ready-hint">Admin review</span>
            <div className="tr-footer-actions">
              <button
                type="button"
                onClick={() => setShowReject(true)}
                disabled={busy}
                className="tr-btn tr-btn--danger"
              >
                Reject with note
              </button>
              <button type="button" onClick={onApprove} disabled={busy} className="tr-btn tr-btn--primary">
                {busy ? "Publishing..." : "Approve & publish"}
              </button>
            </div>
          </>
        )}

        {/* submitted + owner (not admin) */}
        {status === "submitted" && !isAdmin && (
          <span className="tr-muted">Awaiting admin review</span>
        )}

        {/* published */}
        {status === "published" && (
          <span className="tr-muted">Published</span>
        )}
      </footer>

      {/* ── reject modal ── */}
      {showReject && (
        <div className="tr-modal-overlay" onClick={() => setShowReject(false)}>
          <div className="tr-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="tr-modal-title">Reject trail</h3>
            <p className="tr-muted" style={{ marginBottom: 8 }}>
              Explain what needs to change. The engineer will see this note.
            </p>
            <textarea
              className="tr-summary-input"
              rows={4}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="Why is this trail being rejected?"
              autoFocus
            />
            <div className="tr-modal-actions">
              <button type="button" onClick={() => setShowReject(false)} className="tr-btn tr-btn--secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={onReject}
                disabled={!rejectNote.trim() || busy}
                className="tr-btn tr-btn--danger"
              >
                {busy ? "Rejecting..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrailReview;
